import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SettingsIcon from "@mui/icons-material/Settings";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import LetterWheel, { buildWheelTiles, lettersForWheel, shuffleWheelTiles } from "../components/LetterWheel";
import {
    buildCellWordNumbers,
    buildClueMapFromDisplayClue,
    buildDisplayGrid,
    buildWordToNumberMap,
    findHintStartCandidates,
    findWordsAtCell,
    normalizeWord,
    parseWordPositions,
    puzzleCellKeys,
    resolveWordWheelGridSize,
} from "../lib/gridReveal";
import WordWheelApi from "../lib/api";
import {
    formatWordWheelPlayDuration,
    parseWordWheelCatalog,
    WORD_WHEEL_HINT_COST,
} from "../lib/points";
import { DAILY_PATH, DAILY_PLAY_PATH, MAIN_PATH, PLAY_MODE_DAILY, SETTINGS_PATH, WORD_WHEEL_QUEST_TITLE } from "../routes";
import { useWordWheelAppearance } from "../context/WordWheelAppearanceContext";
import {
    isTabletLandscapeView,
    resolveWordWheelView,
    withWordWheelView,
} from "../lib/viewMode";
import { useConfetti } from "../../../context/ConfettiContext";
import { readCoinsEarned, readTotalPuzzleCoinsFromPlay } from "../../../services/PuzzleCoinsUtil";
import WordWheelCompleteDialog from "../components/WordWheelCompleteDialog";
import WordWheelDictionarySheet from "../components/WordWheelDictionarySheet";
import ClueBanner from "../components/ClueBanner";
import useWordWheelWallet from "../hooks/useWordWheelWallet";

const CLUE_PLACEHOLDER = "Tap a numbered cell to see the clue";

const WHEEL_TOOLBAR_WIDTH = 40;

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

export default function WordWheelPlayPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const view = resolveWordWheelView(searchParams.get("view"));
    const tabletLandscape = isTabletLandscapeView(view);
    const isDaily = location.pathname === DAILY_PLAY_PATH
        || location.state?.mode === PLAY_MODE_DAILY;
    const dailyDate = isDaily
        ? (searchParams.get("date") || location.state?.dailyDate || undefined)
        : undefined;
    const backPath = withWordWheelView(
        isDaily
            ? (dailyDate ? `${DAILY_PATH}?date=${encodeURIComponent(dailyDate)}` : DAILY_PATH)
            : MAIN_PATH,
        view,
    );
    const { triggerConfetti } = useConfetti();
    const { playColors } = useWordWheelAppearance();
    const wallet = useWordWheelWallet();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [puzzle, setPuzzle] = useState(null);
    const [foundWords, setFoundWords] = useState([]);
    const [selectedIndices, setSelectedIndices] = useState([]);
    const [playSession, setPlaySession] = useState(null);
    const [selectedWord, setSelectedWord] = useState(null);
    const [wheelTiles, setWheelTiles] = useState([]);
    const [dictionaryOpen, setDictionaryOpen] = useState(false);
    const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
    const [completionStats, setCompletionStats] = useState(null);
    const [coinsCatalogRows, setCoinsCatalogRows] = useState([]);
    const [hintLetters, setHintLetters] = useState(() => new Map());
    const [hintCoinsSpent, setHintCoinsSpent] = useState(0);
    const [hintPending, setHintPending] = useState(false);
    const [shuffleSignal, setShuffleSignal] = useState(0);
    const completionDialogShownRef = useRef(false);

    const targetWords = useMemo(() => parseWords(puzzle?.wordsInUse).map(normalizeWord), [puzzle]);
    const baseWheelLetters = useMemo(
        () => lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details),
        [targetWords, puzzle]
    );
    const wordPositions = useMemo(
        () => parseWordPositions(puzzle?.filledCoordinates),
        [puzzle]
    );
    const puzzleCells = useMemo(() => puzzleCellKeys(wordPositions), [wordPositions]);
    const gridSize = useMemo(() => resolveWordWheelGridSize(puzzle), [puzzle]);
    const displayGrid = useMemo(
        () => buildDisplayGrid(foundWords, wordPositions, hintLetters, gridSize),
        [foundWords, wordPositions, hintLetters, gridSize]
    );
    const hintedCellKeys = useMemo(() => new Set(hintLetters.keys()), [hintLetters]);
    const hintCandidates = useMemo(
        () => findHintStartCandidates(puzzle?.filledCoordinates, foundWords, hintedCellKeys),
        [puzzle?.filledCoordinates, foundWords, hintedCellKeys]
    );
    const puzzleComplete = foundWords.length >= targetWords.length && targetWords.length > 0;
    /** Lifetime Word Wheel Quest coins from profile — same source as GET /home/user puzzleCoins.word_wheel_quest. */
    const lifetimeCoinsRemaining = Math.max(0, wallet.lifetimePoints - hintCoinsSpent);
    const totalHintCoinsAvailable = lifetimeCoinsRemaining + wallet.creditBalance;
    const canUseHint = !puzzleComplete
        && hintCandidates.length > 0
        && totalHintCoinsAvailable >= WORD_WHEEL_HINT_COST
        && !hintPending;
    const hintOnlyCells = useMemo(() => {
        const keys = new Set();
        hintLetters.forEach((_, key) => {
            let fromFound = false;
            for (const w of foundWords) {
                const positions = wordPositions[w];
                if (positions?.some((p) => `${p.row},${p.col}` === key)) {
                    fromFound = true;
                    break;
                }
            }
            if (!fromFound) {
                keys.add(key);
            }
        });
        return keys;
    }, [hintLetters, foundWords, wordPositions]);
    const cellWordNumbers = useMemo(
        () => buildCellWordNumbers(puzzle?.filledCoordinates),
        [puzzle?.filledCoordinates]
    );
    const wordToNumber = useMemo(
        () => buildWordToNumberMap(puzzle?.filledCoordinates, cellWordNumbers),
        [puzzle?.filledCoordinates, cellWordNumbers]
    );
    const clueMap = useMemo(
        () => buildClueMapFromDisplayClue(puzzle?.displayClue),
        [puzzle?.displayClue]
    );
    const selectedWordNumber = selectedWord ? wordToNumber.get(selectedWord) ?? null : null;
    const selectedClue = selectedWord ? (clueMap.get(selectedWord) || "") : "";
    const selectedWordCells = useMemo(() => {
        if (!selectedWord || !wordPositions[selectedWord]) return new Set();
        return new Set(
            wordPositions[selectedWord].map((p) => `${p.row},${p.col}`)
        );
    }, [selectedWord, wordPositions]);
    const selectedWordRevealed = Boolean(
        selectedWord && foundWords.includes(selectedWord)
    );

    const selectedWheelWord = useMemo(
        () => selectedIndices.map((i) => wheelTiles[i]?.letter || "").join(""),
        [selectedIndices, wheelTiles]
    );

    const selectedIndicesRef = useRef(selectedIndices);
    const wheelTilesRef = useRef(wheelTiles);
    wheelTilesRef.current = wheelTiles;

    useEffect(() => {
        setWheelTiles(buildWheelTiles(baseWheelLetters, puzzle?.id || "wheel"));
    }, [puzzle?.id, baseWheelLetters]);

    const setSelection = useCallback((updater) => {
        setSelectedIndices((prev) => {
            const next = typeof updater === "function" ? updater(prev) : updater;
            selectedIndicesRef.current = next;
            return next;
        });
    }, []);

    selectedIndicesRef.current = selectedIndices;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const catalog = await WordWheelApi.fetchCoinsCatalog();
                if (!cancelled) {
                    setCoinsCatalogRows(parseWordWheelCatalog(catalog));
                }
            } catch {
                if (!cancelled) setCoinsCatalogRows([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const data = isDaily
                    ? await WordWheelApi.fetchDaily(dailyDate)
                    : await WordWheelApi.fetchNext();
                if (data?.code === "NO_DATA") {
                    setError(isDaily ? "No daily puzzle available." : "No puzzle available.");
                    return;
                }
                if (cancelled) return;
                setPuzzle(data);

                if (data?.id) {
                    const play = await WordWheelApi.startPlay(data.id);
                    if (!cancelled && play && !play.code) {
                        setPlaySession(play);
                        const saved = (play.wordsFound || "")
                            .split("\n")
                            .map(normalizeWord)
                            .filter(Boolean);
                        setFoundWords(saved);
                    }
                }
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [isDaily, dailyDate]);

    const persistProgress = useCallback(async (words) => {
        if (!puzzle?.id) return null;
        try {
            const updated = await WordWheelApi.updateProgress(puzzle.id, words);
            if (updated && !updated.code) {
                setPlaySession(updated);
                return updated;
            }
            return null;
        } catch (e) {
            setError(e?.message || "Could not save progress");
            return null;
        }
    }, [puzzle]);

    const submitWord = useCallback(async (wordRaw) => {
        const word = normalizeWord(wordRaw);
        if (word.length < 3) {
            setSelectedIndices([]);
            return;
        }
        if (!targetWords.includes(word)) {
            setSelectedIndices([]);
            return;
        }
        if (foundWords.includes(word)) {
            setSelectedIndices([]);
            return;
        }
        const next = [...foundWords, word];
        setFoundWords(next);
        setSelectedIndices([]);
        setSelectedWord(word);
        const updatedSession = await persistProgress(next);
        const puzzleCompleteNow = next.length >= targetWords.length;
        if (puzzleCompleteNow && !completionDialogShownRef.current) {
            completionDialogShownRef.current = true;
            const startedAt = updatedSession?.startedAt ?? playSession?.startedAt;
            const finishedAt = updatedSession?.finishedAt ?? Date.now();
            const rawEarned = readCoinsEarned(updatedSession);
            const adjustedEarned = Math.max(0, rawEarned - hintCoinsSpent);
            setCompletionStats({
                durationLabel: formatWordWheelPlayDuration(startedAt, finishedAt),
                coinsEarned: adjustedEarned,
                hintCoinsSpent,
                totalPuzzleCoins: readTotalPuzzleCoinsFromPlay(updatedSession),
                wordCount: targetWords.length,
            });
            setCompletionDialogOpen(true);
            triggerConfetti(8000);
            wallet.refresh({ silent: true }).catch(() => {});
        }
    }, [targetWords, foundWords, persistProgress, playSession?.startedAt, triggerConfetti, hintCoinsSpent, wallet]);

    const handleDragEnd = useCallback(() => {
        const word = selectedIndicesRef.current
            .map((i) => wheelTilesRef.current[i]?.letter || "")
            .join("");
        if (word.length >= 3) {
            submitWord(word);
        }
    }, [submitWord]);

    const handleCellClick = useCallback((row, col) => {
        const matches = findWordsAtCell(puzzle?.filledCoordinates, row, col, cellWordNumbers);
        if (matches.length === 0) return;
        setSelectedWord(matches[0].word);
    }, [puzzle?.filledCoordinates, cellWordNumbers]);

    const handleShuffleLetters = useCallback(() => {
        setSelectedIndices([]);
        setShuffleSignal((n) => n + 1);
    }, []);

    const applyWheelShuffle = useCallback(() => {
        setWheelTiles((prev) =>
            shuffleWheelTiles(prev.length ? prev : buildWheelTiles(baseWheelLetters, puzzle?.id || "wheel")),
        );
    }, [baseWheelLetters, puzzle?.id]);

    const handleHint = useCallback(async () => {
        if (!canUseHint) return;
        const pick = hintCandidates[Math.floor(Math.random() * hintCandidates.length)];
        if (!pick) return;

        setHintPending(true);
        try {
            if (lifetimeCoinsRemaining >= WORD_WHEEL_HINT_COST) {
                setHintCoinsSpent((prev) => prev + WORD_WHEEL_HINT_COST);
            } else if (wallet.creditBalance >= WORD_WHEEL_HINT_COST) {
                await wallet.consumeHintCredits({
                    playId: playSession?.id,
                    creditsConsumed: WORD_WHEEL_HINT_COST,
                });
            } else {
                return;
            }

            setHintLetters((prev) => {
                const next = new Map(prev);
                next.set(pick.key, pick.letter);
                return next;
            });
            setSelectedWord(pick.word);
        } catch (e) {
            setError(e?.message || "Could not use hint");
        } finally {
            setHintPending(false);
        }
    }, [
        canUseHint,
        hintCandidates,
        wallet,
        lifetimeCoinsRemaining,
        playSession?.id,
    ]);

    const toolbarButtonSx = {
        width: 34,
        height: 34,
        minWidth: 34,
        minHeight: 34,
        p: 0.5,
        bgcolor: "rgba(255, 255, 255, 0.88)",
        border: `1px solid ${playColors.borderStrong}`,
        boxShadow: playColors.shadowSm,
        color: "#2E8B57",
        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.96)" },
        "&.Mui-disabled": {
            opacity: 1,
            bgcolor: "rgba(255, 255, 255, 0.96)",
            color: "rgba(15, 61, 54, 0.45)",
            border: "1px solid rgba(15, 61, 54, 0.28)",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.08)",
        },
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
                <IconButton
                    component={RouterLink}
                    to={backPath}
                    aria-label="Back"
                    sx={{ alignSelf: "flex-start", color: playColors.text, ml: -1, minWidth: 44, minHeight: 44 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <CircularProgress sx={{ color: playColors.accent }} />
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
                // Mobile-style: allow scroll if needed, but never show a desktop scrollbar.
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
            }}
        >
            <Stack direction="row" alignItems="flex-start" sx={{ flexShrink: 0, pb: 1.5 }}>
                <IconButton
                    component={RouterLink}
                    to={backPath}
                    aria-label="Back"
                    sx={{ flexShrink: 0, color: playColors.text, ml: -1, minWidth: 44, minHeight: 44 }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, textAlign: "center", minWidth: 0, pr: 5.5 }}>
                    <Typography
                        variant="overline"
                        sx={{
                            display: "block",
                            letterSpacing: 2,
                            fontWeight: 600,
                            color: playColors.textSecondary,
                            fontSize: "0.65rem",
                            lineHeight: 1.4,
                        }}
                    >
                        {isDaily ? "DAILY PUZZLE" : "WORD WHEEL QUEST"}
                        {tabletLandscape ? " · TABLET" : ""}
                    </Typography>
                    {isDaily && puzzle?.dailyDayNumber != null && (
                        <Typography
                            variant="caption"
                            sx={{
                                display: "block",
                                color: playColors.textSecondary,
                                fontWeight: 600,
                                mt: 0.25,
                            }}
                        >
                            Day {puzzle.dailyDayNumber}
                            {puzzle.dailyPlayDate ? ` · ${puzzle.dailyPlayDate}` : ""}
                        </Typography>
                    )}
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 800,
                            fontSize: tabletLandscape ? "1.35rem" : "1.125rem",
                            lineHeight: 1.25,
                            color: playColors.text,
                            px: 1,
                        }}
                    >
                        {puzzle?.title || (isDaily ? "Daily Puzzle" : WORD_WHEEL_QUEST_TITLE)}
                    </Typography>
                </Box>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 1, flexShrink: 0 }}>{error}</Alert>}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: tabletLandscape ? "row" : "column",
                    gap: tabletLandscape ? 2.5 : 0,
                    alignItems: "stretch",
                }}
            >
            <Box
                sx={{
                    flex: tabletLandscape ? "1 1 52%" : undefined,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
            <Box
                sx={{
                    flexShrink: tabletLandscape ? 1 : 0,
                    flexGrow: tabletLandscape ? 1 : 0,
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: tabletLandscape ? "center" : "flex-start",
                    minHeight: 0,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: tabletLandscape ? 480 : "100%",
                        maxHeight: tabletLandscape ? "100%" : undefined,
                        aspectRatio: "1 / 1",
                        display: "grid",
                        gridTemplateColumns: `repeat(${gridSize}, 1fr)`,
                        gridTemplateRows: `repeat(${gridSize}, 1fr)`,
                        gap: tabletLandscape ? "5px" : "4px",
                    }}
                >
                    {Array.from({ length: gridSize }, (_, row) =>
                        Array.from({ length: gridSize }, (_, col) => {
                            const letter = displayGrid[row]?.[col] || "";
                            const cellKey = `${row},${col}`;
                            const isPuzzleCell = puzzleCells.has(cellKey);
                            const isRevealed = Boolean(letter);
                            const isHintRevealed = isRevealed && hintOnlyCells.has(cellKey);
                            const isSelected = selectedWordCells.has(cellKey);
                            const wordNumber = cellWordNumbers.get(`${row},${col}`);
                            return (
                                <Box
                                    key={`${row}-${col}`}
                                    onClick={isPuzzleCell ? () => handleCellClick(row, col) : undefined}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        aspectRatio: "1 / 1",
                                        position: "relative",
                                        fontSize: tabletLandscape
                                            ? "clamp(14px, 2.2vmin, 22px)"
                                            : "clamp(10px, 3.2vmin, 18px)",
                                        fontWeight: 700,
                                        borderRadius: 1.25,
                                        cursor: isPuzzleCell ? "pointer" : "default",
                                        bgcolor: !isPuzzleCell
                                            ? "transparent"
                                            : isRevealed
                                                ? isHintRevealed
                                                    ? "rgba(254, 243, 199, 0.95)"
                                                    : playColors.successSoft
                                                : playColors.gridCellHidden,
                                        color: isRevealed
                                            ? isHintRevealed
                                                ? "#92400e"
                                                : playColors.successText
                                            : "transparent",
                                        border: isPuzzleCell
                                            ? `2px solid ${
                                                isSelected
                                                    ? playColors.accent
                                                    : isRevealed
                                                        ? isHintRevealed
                                                            ? "#fcd34d"
                                                            : "#bbf7d0"
                                                        : playColors.borderStrong
                                            }`
                                            : "none",
                                        boxShadow: isPuzzleCell && !isRevealed ? playColors.shadowSm : "none",
                                        minWidth: 0,
                                        minHeight: 0,
                                    }}
                                >
                                    {wordNumber && isPuzzleCell && (
                                        <Box
                                            component="span"
                                            sx={{
                                                position: "absolute",
                                                top: 1,
                                                left: 1,
                                                minWidth: 16,
                                                minHeight: 16,
                                                px: 0.35,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                lineHeight: 1,
                                                fontSize: "clamp(10px, 2.8vmin, 13px)",
                                                fontWeight: 800,
                                                color: "#064e3b",
                                                bgcolor: "rgba(255, 255, 255, 0.96)",
                                                border: "1px solid #059669",
                                                borderRadius: "4px",
                                                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.28)",
                                                zIndex: 2,
                                                userSelect: "none",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            {wordNumber}
                                        </Box>
                                    )}
                                    {letter}
                                </Box>
                            );
                        })
                    )}
                </Box>
            </Box>

            <ClueBanner
                clueText={
                    selectedWord
                        ? `${selectedWordNumber != null ? `${selectedWordNumber}. ` : ""}${selectedClue || "No clue available for this word."}`
                        : CLUE_PLACEHOLDER
                }
                hasClue={Boolean(selectedWord)}
                textColor={playColors.textOnSurface}
                overlay={
                    selectedWheelWord ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 1.5,
                                borderRadius: 2,
                                bgcolor: "rgba(255, 255, 255, 0.55)",
                                backdropFilter: "blur(2px)",
                                pointerEvents: "none",
                            }}
                        >
                            <Typography
                                aria-live="polite"
                                sx={{
                                    fontWeight: 800,
                                    fontSize: "clamp(1.5rem, 7vw, 2.125rem)",
                                    letterSpacing: "0.14em",
                                    color: playColors.textOnSurface,
                                    opacity: 0.88,
                                    textTransform: "uppercase",
                                    lineHeight: 1.1,
                                }}
                            >
                                {selectedWheelWord}
                            </Typography>
                        </Box>
                    ) : null
                }
            />
            </Box>

            <Box
                sx={{
                    flex: tabletLandscape ? "1 1 48%" : undefined,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    mt: tabletLandscape ? 0 : 2.5,
                    maxWidth: tabletLandscape ? "none" : 360,
                    mx: "auto",
                    minHeight: 0,
                }}
            >
                <Stack
                    direction="column"
                    spacing={0.75}
                    sx={{ width: WHEEL_TOOLBAR_WIDTH, flexShrink: 0, alignItems: "center", justifyContent: "center" }}
                >
                    <Tooltip
                        title={
                            puzzleComplete
                                ? "Puzzle complete"
                                : hintCandidates.length === 0
                                    ? "No hints left"
                                    : totalHintCoinsAvailable < WORD_WHEEL_HINT_COST
                                        ? `Not enough coins (${lifetimeCoinsRemaining} left)`
                                        : `Reveal a starting letter (−${WORD_WHEEL_HINT_COST} coins)`
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleHint}
                                disabled={!canUseHint}
                                aria-label="Hint"
                                sx={toolbarButtonSx}
                            >
                                <LightbulbOutlinedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="caption"
                        sx={{
                            fontSize: 10,
                            fontWeight: 800,
                            lineHeight: 1.1,
                            textAlign: "center",
                            color: lifetimeCoinsRemaining < WORD_WHEEL_HINT_COST
                                && wallet.creditBalance < WORD_WHEEL_HINT_COST
                                ? "rgba(185, 28, 28, 0.85)"
                                : "rgba(15, 61, 54, 0.72)",
                            maxWidth: WHEEL_TOOLBAR_WIDTH,
                        }}
                    >
                        {lifetimeCoinsRemaining} coins
                    </Typography>
                </Stack>

                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            justifyContent: "center",
                            height: tabletLandscape ? "min(52vh, 360px)" : "min(72vw, 240px)",
                            maxWidth: tabletLandscape ? 360 : 280,
                            mx: "auto",
                        }}
                    >
                    {wheelTiles.length > 0 ? (
                        <LetterWheel
                            tiles={wheelTiles}
                            selectedIndices={selectedIndices}
                            onSelectionChange={setSelection}
                            onDragEnd={handleDragEnd}
                            onShuffle={applyWheelShuffle}
                            shuffleSignal={shuffleSignal}
                            colors={playColors}
                            fill
                        />
                    ) : (
                        <Typography sx={{ textAlign: "center", color: playColors.textMuted }}>
                            No letters in puzzle
                        </Typography>
                    )}
                </Box>

                <Stack
                    direction="column"
                    spacing={0.75}
                    sx={{ width: WHEEL_TOOLBAR_WIDTH, flexShrink: 0, alignItems: "center", justifyContent: "center" }}
                >
                    <Tooltip title="Dictionary">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => setDictionaryOpen(true)}
                                disabled={!selectedWord}
                                aria-label="Dictionary"
                                sx={toolbarButtonSx}
                            >
                                <MenuBookIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Shuffle letters">
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleShuffleLetters}
                                disabled={wheelTiles.length < 2}
                                aria-label="Shuffle letters"
                                sx={toolbarButtonSx}
                            >
                                <ShuffleIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Settings">
                        <span>
                            <IconButton
                                    size="small"
                                    onClick={() =>
                                        navigate(withWordWheelView(SETTINGS_PATH, view), {
                                            state: {
                                                backPath: withWordWheelView(
                                                    `${location.pathname}${location.search || ""}`,
                                                    view,
                                                ),
                                            },
                                        })
                                    }
                                    aria-label="Settings"
                                    sx={toolbarButtonSx}
                                >
                                    <SettingsIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Stack>
                </Box>
            </Box>

            <WordWheelDictionarySheet
                open={dictionaryOpen}
                onClose={() => setDictionaryOpen(false)}
                word={selectedWord || ""}
                wordRevealed={selectedWordRevealed}
                language={puzzle?.language || "english"}
                colors={playColors}
            />

            <WordWheelCompleteDialog
                open={completionDialogOpen}
                onClose={() => setCompletionDialogOpen(false)}
                durationLabel={completionStats?.durationLabel}
                coinsEarned={completionStats?.coinsEarned ?? 0}
                hintCoinsSpent={completionStats?.hintCoinsSpent ?? 0}
                totalPuzzleCoins={completionStats?.totalPuzzleCoins}
                wordCount={completionStats?.wordCount}
                coinsRows={coinsCatalogRows}
                colors={playColors}
            />

            {playSession?.isCompleted && !completionDialogOpen && (
                <Typography
                    variant="caption"
                    sx={{ display: "block", textAlign: "center", pb: 0.5, color: playColors.success, flexShrink: 0 }}
                >
                    Play recorded
                </Typography>
            )}
        </Box>
    );
}
